import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  AddPhotoAlternate as AddPhotoAlternateIcon,
  AutoAwesome as AutoAwesomeIcon,
  DeleteOutlineOutlined as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  PhotoAlbum as PhotoAlbumIcon,
  SaveOutlined as SaveOutlinedIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import api from "../../api/api";

const EMPTY_ALBUM_FORM = {
  AlbumTitle: "",
  AlbumDescription: "",
  EventDate: "",
  IsActive: true,
  CoverImage: null,
  ParentFolderId: "",
};

const EMPTY_UPLOAD_ROW = {
  files: [],
  description: "",
  startDisplayOrder: "",
};

function resolveMediaType(value) {
  const normalizedValue = String(value || "").toLowerCase();

  if (normalizedValue.includes("video")) {
    return "video";
  }

  return "image";
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / 1024 ** unitIndex;

  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function getErrorMessage(error, fallbackMessage) {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (status === 413) {
    return "Upload failed: the selected files are too large for the server limit. Reduce the batch size or compress the images and try again.";
  }

  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (data?.message) {
    return data.message;
  }

  const validationErrors = data?.errors;

  if (validationErrors && typeof validationErrors === "object") {
    const firstError = Object.values(validationErrors).find(
      (value) => Array.isArray(value) && value.length > 0,
    );

    if (firstError?.[0]) {
      return firstError[0];
    }
  }

  return error?.message || fallbackMessage;
}

function normalizeBoolean(value, fallback = true) {
  if ([true, 1, "1", "true"].includes(value)) {
    return true;
  }

  if ([false, 0, "0", "false"].includes(value)) {
    return false;
  }

  return fallback;
}

function normalizeAlbum(rawAlbum) {
  return {
    id: rawAlbum?.AlbumId || rawAlbum?.albumId || rawAlbum?.id,
    title: rawAlbum?.AlbumTitle || rawAlbum?.albumTitle || "",
    description: rawAlbum?.AlbumDescription || rawAlbum?.albumDescription || "",
    eventDate: (rawAlbum?.EventDate || rawAlbum?.eventDate || "").slice(0, 10),
    coverImage: rawAlbum?.CoverImage || rawAlbum?.coverImage || "",
    parentFolderId:
      rawAlbum?.ParentFolderId ?? rawAlbum?.parentFolderId ?? rawAlbum?.parentId ?? "",
    isActive: normalizeBoolean(rawAlbum?.IsActive ?? rawAlbum?.isActive, true),
    imagesCount: Number(rawAlbum?.ImagesCount || rawAlbum?.imagesCount || 0),
    raw: rawAlbum,
  };
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function extractAlbums(payload) {
  return [
    ...toArray(payload?.albums),
    ...toArray(payload?.data?.albums),
    ...toArray(payload?.galleryAlbums),
    ...toArray(payload?.data?.galleryAlbums),
    ...(Array.isArray(payload) ? payload : []),
  ];
}

function extractAlbum(payload) {
  return (
    payload?.album ||
    payload?.data?.album ||
    payload?.galleryAlbum ||
    payload?.data?.galleryAlbum ||
    payload?.data ||
    payload
  );
}

function extractChildAlbums(payload) {
  return [
    ...toArray(payload?.folders),
    ...toArray(payload?.data?.folders),
    ...toArray(payload?.album?.albums),
    ...toArray(payload?.album?.folders),
    ...toArray(payload?.album?.galleryAlbums),
    ...toArray(payload?.album?.GalleryAlbums),
    ...toArray(payload?.data?.album?.albums),
    ...toArray(payload?.data?.album?.folders),
    ...toArray(payload?.data?.album?.galleryAlbums),
    ...toArray(payload?.galleryAlbum?.albums),
    ...toArray(payload?.galleryAlbum?.folders),
    ...toArray(payload?.galleryAlbum?.galleryAlbums),
    ...toArray(payload?.galleryAlbum?.GalleryAlbums),
    ...toArray(payload?.data?.galleryAlbum?.albums),
    ...toArray(payload?.data?.galleryAlbum?.folders),
    ...toArray(payload?.data?.galleryAlbum?.galleryAlbums),
  ];
}

function extractImages(payload) {
  return [
    ...toArray(payload?.images),
    ...toArray(payload?.data?.images),
    ...toArray(payload?.galleryImages),
    ...toArray(payload?.data?.galleryImages),
    ...toArray(payload?.album?.images),
    ...toArray(payload?.album?.galleryImages),
    ...toArray(payload?.album?.GalleryImages),
    ...toArray(payload?.data?.album?.images),
    ...toArray(payload?.data?.album?.galleryImages),
    ...toArray(payload?.galleryAlbum?.images),
    ...toArray(payload?.galleryAlbum?.galleryImages),
  ];
}

function normalizeImage(rawImage) {
  return {
    id:
      rawImage?.GalleryImageId || rawImage?.galleryImageId || rawImage?.ImageId,
    title: rawImage?.ImageTitle || rawImage?.imageTitle || "",
    description: rawImage?.ImageDescription || rawImage?.imageDescription || "",
    imageUrl:
      rawImage?.ImageUrl ||
      rawImage?.imageUrl ||
      rawImage?.ImagePath ||
      rawImage?.imagePath ||
      rawImage?.FilePath ||
      rawImage?.filePath ||
      rawImage?.Url ||
      rawImage?.url ||
      "",
    imageType: resolveMediaType(rawImage?.ImageType || rawImage?.imageType),
    displayOrder: rawImage?.DisplayOrder ?? rawImage?.displayOrder ?? 0,
    isActive: normalizeBoolean(rawImage?.IsActive ?? rawImage?.isActive, true),
    raw: rawImage,
  };
}

function buildAlbumFormData(form) {
  const formData = new FormData();
  formData.append("AlbumTitle", form.AlbumTitle);
  formData.append("AlbumDescription", form.AlbumDescription);
  formData.append("EventDate", form.EventDate || "");
  formData.append("IsActive", form.IsActive ? "1" : "0");
  formData.append("ParentFolderId", form.ParentFolderId || "");

  if (form.CoverImage) {
    formData.append("CoverImage", form.CoverImage);
  }

  return formData;
}

function buildImagesFormData(rows) {
  const formData = new FormData();

  rows.forEach((row) => {
    row.files.forEach((file, index) => {
      const mediaType = resolveMediaType(file?.type);

      formData.append("Images[]", file);
      formData.append("ImageTitles[]", "");
      formData.append("ImageDescriptions[]", row.description || "");
      formData.append("ImageType[]", mediaType);
      formData.append(
        "DisplayOrders[]",
        row.startDisplayOrder === ""
          ? String(index)
          : String(Number(row.startDisplayOrder) + index),
      );
    });
  });

  return formData;
}

function AlbumSummaryCard({ label, value, tone = "default" }) {
  const gradients = {
    default: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    warm: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
    success: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 4,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        background: gradients[tone] || gradients.default,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={800} color="#0f172a">
        {value}
      </Typography>
    </Paper>
  );
}

function AddGalleryImages() {
  const currentUser = useSelector((state) => state.user.user) || {};
  const token =
    currentUser.token || currentUser.accessToken || currentUser.jwtToken || "";

  const [albums, setAlbums] = useState([]);
  const [selectedAlbumId, setSelectedAlbumId] = useState("");
  const [uploadFolderPath, setUploadFolderPath] = useState([]);
  const [selectedAlbumImages, setSelectedAlbumImages] = useState([]);
  const [selectedAlbumFolders, setSelectedAlbumFolders] = useState([]);
  const [albumForm, setAlbumForm] = useState(EMPTY_ALBUM_FORM);
  const [uploadRows, setUploadRows] = useState([{ ...EMPTY_UPLOAD_ROW }]);
  const [loading, setLoading] = useState(true);
  const [albumLoading, setAlbumLoading] = useState(false);
  const [submittingAlbum, setSubmittingAlbum] = useState(false);
  const [submittingImages, setSubmittingImages] = useState(false);
  const [pendingAlbumDeleteId, setPendingAlbumDeleteId] = useState("");
  const [pendingImageDeleteId, setPendingImageDeleteId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedCoverName, setSelectedCoverName] = useState("");

  const selectedAlbum = useMemo(
    () =>
      albums.find((album) => String(album.id) === String(selectedAlbumId)) ||
      null,
    [albums, selectedAlbumId],
  );

  const albumsById = useMemo(
    () =>
      new Map(albums.map((album) => [String(album.id), album])),
    [albums],
  );

  const albumChildrenByParent = useMemo(() => {
    const groupedAlbums = new Map();

    albums.forEach((album) => {
      const parentKey = String(album.parentFolderId || "");
      const siblings = groupedAlbums.get(parentKey) || [];
      siblings.push(album);
      groupedAlbums.set(parentKey, siblings);
    });

    groupedAlbums.forEach((siblings, key) => {
      groupedAlbums.set(
        key,
        [...siblings].sort((first, second) => first.title.localeCompare(second.title)),
      );
    });

    return groupedAlbums;
  }, [albums]);

  const albumOptions = useMemo(
    () =>
      albums.map((album) => ({
        ...album,
        optionLabel: `${"  ".repeat(album.depth || 0)}${album.title || "Untitled folder"}`,
      })),
    [albums],
  );

  const summary = useMemo(
    () => ({
      totalAlbums: albums.length,
      activeAlbums: albums.filter((album) => album.isActive).length,
      totalImages: albums.reduce(
        (total, album) => total + Number(album.imagesCount || 0),
        0,
      ),
    }),
    [albums],
  );

  const selectedUploadFiles = useMemo(
    () => uploadRows.flatMap((row) => row.files),
    [uploadRows],
  );

  const totalUploadSize = useMemo(
    () =>
      selectedUploadFiles.reduce(
        (total, file) => total + Number(file?.size || 0),
        0,
      ),
    [selectedUploadFiles],
  );

  const buildAlbumPath = (albumId) => {
    if (!albumId) {
      return [];
    }

    const path = [];
    let currentAlbum = albumsById.get(String(albumId));
    const visited = new Set();

    while (currentAlbum && !visited.has(String(currentAlbum.id))) {
      visited.add(String(currentAlbum.id));
      path.unshift(String(currentAlbum.id));

      if (!currentAlbum.parentFolderId) {
        break;
      }

      currentAlbum = albumsById.get(String(currentAlbum.parentFolderId));
    }

    return path;
  };

  const uploadTargetAlbumId = uploadFolderPath[uploadFolderPath.length - 1] || "";

  const uploadTargetAlbum = useMemo(
    () => (uploadTargetAlbumId ? albumsById.get(String(uploadTargetAlbumId)) || null : null),
    [albumsById, uploadTargetAlbumId],
  );

  const uploadCascadeLevels = useMemo(() => {
    const levels = [];
    let parentKey = "";
    let depth = 0;

    while (true) {
      const options = albumChildrenByParent.get(parentKey) || [];

      if (options.length === 0) {
        break;
      }

      const selectedId = uploadFolderPath[depth] || "";

      levels.push({
        depth,
        options,
        selectedId,
      });

      if (!selectedId) {
        break;
      }

      parentKey = String(selectedId);
      depth += 1;
    }

    return levels;
  }, [albumChildrenByParent, uploadFolderPath]);

  const loadAlbumTree = async () => {
    const response = await api.getGalleryAlbums({}, token);
    const responseData = response?.data || response || {};
    const rootAlbums = extractAlbums(responseData).map(normalizeAlbum);
    const visited = new Set();
    const flattenedAlbums = [];

    const visitAlbum = async (album, depth = 0, parentPath = "") => {
      if (!album?.id || visited.has(String(album.id))) {
        return;
      }

      visited.add(String(album.id));

      const title = album.title || "Untitled folder";
      const path = parentPath ? `${parentPath} / ${title}` : title;

      flattenedAlbums.push({
        ...album,
        depth,
        path,
      });

      try {
        const detailResponse = await api.getGalleryAlbumById(album.id, {}, token);
        const detailData = detailResponse?.data || detailResponse || {};
        const childAlbums = extractChildAlbums(detailData)
          .map(normalizeAlbum)
          .filter((childAlbum) => String(childAlbum.id) !== String(album.id))
          .sort((first, second) => first.title.localeCompare(second.title));

        for (const childAlbum of childAlbums) {
          await visitAlbum(
            {
              ...childAlbum,
              parentFolderId: childAlbum.parentFolderId || album.id,
            },
            depth + 1,
            path,
          );
        }
      } catch {
        // Keep the current tree usable even if one branch fails to expand.
      }
    };

    const sortedRoots = rootAlbums.sort((first, second) =>
      first.title.localeCompare(second.title),
    );

    for (const rootAlbum of sortedRoots) {
      await visitAlbum(rootAlbum);
    }

    return flattenedAlbums;
  };

  useEffect(() => {
    let active = true;

    async function loadAlbums() {
      try {
        setLoading(true);
        setError("");
        const normalizedAlbums = await loadAlbumTree();

        if (active) {
          setAlbums(normalizedAlbums);
          if (normalizedAlbums[0]?.id) {
            setSelectedAlbumId(String(normalizedAlbums[0].id));
          }
        }
      } catch (loadError) {
        if (active) {
          setError(getErrorMessage(loadError, "Unable to load gallery albums."));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadAlbums();

    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    let active = true;

    async function loadAlbumDetails() {
      if (!selectedAlbumId) {
        setSelectedAlbumImages([]);
        setSelectedAlbumFolders([]);
        return;
      }

      try {
        setAlbumLoading(true);
        const response = await api.getGalleryAlbumById(
          selectedAlbumId,
          {},
          token,
        );
        const responseData = response?.data || response || {};
        const imageList = extractImages(responseData);
        const folderList = extractChildAlbums(responseData).map(normalizeAlbum);

        if (active) {
          setSelectedAlbumImages(imageList.map(normalizeImage));
          setSelectedAlbumFolders(
            folderList
              .filter((folder) => String(folder.id) !== String(selectedAlbumId))
              .sort((first, second) => first.title.localeCompare(second.title)),
          );
        }
      } catch (loadError) {
        if (active) {
          setError(getErrorMessage(loadError, "Unable to load folder details."));
        }
      } finally {
        if (active) {
          setAlbumLoading(false);
        }
      }
    }

    loadAlbumDetails();

    return () => {
      active = false;
    };
  }, [selectedAlbumId, token]);

  useEffect(() => {
    if (!selectedAlbumId || albums.length === 0) {
      return;
    }

    setUploadFolderPath(buildAlbumPath(selectedAlbumId));
  }, [selectedAlbumId, albums]);

  const handleAlbumFormChange = (field, value) => {
    setAlbumForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUploadRowChange = (index, field, value) => {
    setUploadRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );
  };

  const handleUploadFolderLevelChange = (levelIndex, value) => {
    setUploadFolderPath((prev) => {
      const nextPath = prev.slice(0, levelIndex);

      if (value) {
        nextPath[levelIndex] = value;
      }

      return nextPath;
    });
  };

  const refreshAlbums = async (nextAlbumId) => {
    const normalizedAlbums = await loadAlbumTree();

    setAlbums(normalizedAlbums);

    if (nextAlbumId) {
      setSelectedAlbumId(String(nextAlbumId));
      return;
    }

    if (
      selectedAlbumId &&
      normalizedAlbums.some((album) => String(album.id) === String(selectedAlbumId))
    ) {
      return;
    }

    setSelectedAlbumId(normalizedAlbums[0]?.id ? String(normalizedAlbums[0].id) : "");
  };

  const handleCreateAlbum = async () => {
    if (!albumForm.AlbumTitle.trim()) {
      setError("Folder title is required.");
      return;
    }

    try {
      setSubmittingAlbum(true);
      setError("");
      setMessage("");
      const response = await api.createGalleryAlbum(
        buildAlbumFormData(albumForm),
        token,
      );
      const responseData = response?.data || response || {};
      const createdAlbum = normalizeAlbum(responseData?.album || {});
      await refreshAlbums(createdAlbum.id);
      setAlbumForm(EMPTY_ALBUM_FORM);
      setSelectedCoverName("");
      setMessage(responseData?.message || "Folder created successfully.");
    } catch (submitError) {
      setError(getErrorMessage(submitError, "Unable to create folder."));
    } finally {
      setSubmittingAlbum(false);
    }
  };

  const handleUpdateAlbum = async () => {
    if (!selectedAlbum?.id) {
      setError("Select a folder to update.");
      return;
    }

    try {
      setSubmittingAlbum(true);
      setError("");
      setMessage("");
      const response = await api.updateGalleryAlbum(
        selectedAlbum.id,
        buildAlbumFormData({
          AlbumTitle: selectedAlbum.title,
          AlbumDescription: selectedAlbum.description,
          EventDate: selectedAlbum.eventDate,
          IsActive: selectedAlbum.isActive,
          CoverImage: null,
          ParentFolderId: selectedAlbum.parentFolderId || "",
        }),
        token,
      );
      const responseData = response?.data || response || {};
      await refreshAlbums(selectedAlbum.id);
      setMessage(responseData?.message || "Folder updated successfully.");
    } catch (submitError) {
      setError(getErrorMessage(submitError, "Unable to update folder."));
    } finally {
      setSubmittingAlbum(false);
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    try {
      setPendingAlbumDeleteId(String(albumId));
      setError("");
      setMessage("");
      await api.deleteGalleryAlbum(albumId, token);
      await refreshAlbums();
      setSelectedAlbumImages([]);
      setSelectedAlbumFolders([]);
      setMessage("Folder deleted successfully.");
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "Unable to delete folder."));
    } finally {
      setPendingAlbumDeleteId("");
    }
  };

  const handleUploadImages = async () => {
    if (!uploadTargetAlbumId) {
      setError("Create or select a folder before uploading media.");
      return;
    }

    const validRows = uploadRows.filter((row) => row.files.length > 0);

    if (validRows.length === 0) {
      setError("Choose one or more image or video files.");
      return;
    }

    try {
      setSubmittingImages(true);
      setError("");
      setMessage("");
      const response = await api.addGalleryAlbumImages(
        uploadTargetAlbumId,
        buildImagesFormData(validRows),
        token,
      );
      const responseData = response?.data || response || {};
      await refreshAlbums(uploadTargetAlbumId);
      const albumResponse = await api.getGalleryAlbumById(
        uploadTargetAlbumId,
        {},
        token,
      );
      const albumData = albumResponse?.data || albumResponse || {};
      setSelectedAlbumImages(extractImages(albumData).map(normalizeImage));
      setSelectedAlbumFolders(
        extractChildAlbums(albumData)
          .map(normalizeAlbum)
          .filter((folder) => String(folder.id) !== String(uploadTargetAlbumId))
          .sort((first, second) => first.title.localeCompare(second.title)),
      );
      setUploadRows([{ ...EMPTY_UPLOAD_ROW }]);
      setMessage(responseData?.message || "Media uploaded successfully.");
    } catch (submitError) {
      setError(getErrorMessage(submitError, "Unable to upload media."));
    } finally {
      setSubmittingImages(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      setPendingImageDeleteId(String(imageId));
      setError("");
      setMessage("");
      await api.deleteGalleryImage(imageId, token);
      await refreshAlbums(selectedAlbum?.id);
      setSelectedAlbumImages((prev) =>
        prev.filter((image) => String(image.id) !== String(imageId)),
      );
      setMessage("Image deleted successfully.");
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "Unable to delete image."));
    } finally {
      setPendingImageDeleteId("");
    }
  };

  const handleSelectedAlbumFieldChange = (field, value) => {
    setAlbums((prev) =>
      prev.map((album) =>
        String(album.id) === String(selectedAlbumId)
          ? {
              ...album,
              [field]: value,
            }
          : album,
      ),
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 5 },
        background:
          "radial-gradient(circle at top left, rgba(59, 130, 246, 0.10), transparent 32%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: 5,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              background:
                "linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.96) 100%)",
              color: "white",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Stack spacing={2}>
              <Chip
                icon={<AutoAwesomeIcon />}
                label="Admin gallery workspace"
                sx={{
                  alignSelf: "flex-start",
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "white",
                  "& .MuiChip-icon": { color: "#93c5fd" },
                }}
              />
              <Box>
                <Typography variant="h4" fontWeight={800}>
                  Create folders and publish gallery media
                </Typography>
                <Typography sx={{ mt: 1.25, color: "rgba(255,255,255,0.76)" }}>
                  Build a root folder or nested folder, set the cover, then add
                  batches of images or videos inside any folder from one page.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <AlbumSummaryCard
                label="Folders"
                value={summary.totalAlbums}
                tone="default"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <AlbumSummaryCard
                label="Active folders"
                value={summary.activeAlbums}
                tone="success"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <AlbumSummaryCard
                label="Published items"
                value={summary.totalImages}
                tone="warm"
              />
            </Grid>
          </Grid>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {message ? <Alert severity="success">{message}</Alert> : null}

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 5 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
                }}
              >
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      Create New Folder
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                      Use this form for a root folder or subfolder, then upload
                      media into any folder you select.
                    </Typography>
                  </Box>

                  <TextField
                    label="Folder title"
                    value={albumForm.AlbumTitle}
                    onChange={(event) =>
                      handleAlbumFormChange("AlbumTitle", event.target.value)
                    }
                    fullWidth
                  />
                  <TextField
                    select
                    label="Parent folder"
                    value={albumForm.ParentFolderId}
                    onChange={(event) =>
                      handleAlbumFormChange("ParentFolderId", event.target.value)
                    }
                    fullWidth
                  >
                    <MenuItem value="">Root folder</MenuItem>
                    {albumOptions.map((album) => (
                      <MenuItem key={album.id} value={String(album.id)}>
                        {album.optionLabel}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Folder description"
                    value={albumForm.AlbumDescription}
                    onChange={(event) =>
                      handleAlbumFormChange(
                        "AlbumDescription",
                        event.target.value,
                      )
                    }
                    fullWidth
                    multiline
                    minRows={4}
                  />
                  <TextField
                    label="Event date"
                    type="date"
                    value={albumForm.EventDate}
                    onChange={(event) =>
                      handleAlbumFormChange("EventDate", event.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography fontWeight={700}>Active folder</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inactive folders stay hidden from active gallery views.
                      </Typography>
                    </Box>
                    <Switch
                      checked={albumForm.IsActive}
                      onChange={(event) =>
                        handleAlbumFormChange("IsActive", event.target.checked)
                      }
                    />
                  </Stack>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<PhotoAlbumIcon />}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    {selectedCoverName ? "Change cover image" : "Upload cover image"}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(event) => {
                        const file = event.target.files?.[0] || null;
                        handleAlbumFormChange("CoverImage", file);
                        setSelectedCoverName(file?.name || "");
                      }}
                    />
                  </Button>
                  {selectedCoverName ? (
                    <Typography variant="body2" color="text.secondary">
                      Selected cover: {selectedCoverName}
                    </Typography>
                  ) : null}
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleCreateAlbum}
                    disabled={submittingAlbum}
                    startIcon={
                      submittingAlbum ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveOutlinedIcon />
                      )
                    }
                    sx={{
                      borderRadius: 999,
                      py: 1.4,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    {submittingAlbum ? "Creating folder..." : "Create Folder"}
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, lg: 7 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 3 },
                  borderRadius: 4,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
                }}
              >
                <Stack spacing={2.5}>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    spacing={1.5}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={800}>
                        Manage Existing Folders
                      </Typography>
                      <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                        Select any folder, adjust its metadata, create a child
                        folder, then add more images or videos to it.
                      </Typography>
                    </Box>
                    {loading ? <CircularProgress size={24} /> : null}
                  </Stack>

                  <TextField
                    select
                    label="Select folder"
                    value={selectedAlbumId}
                    onChange={(event) => setSelectedAlbumId(event.target.value)}
                    fullWidth
                    disabled={loading || albums.length === 0}
                  >
                    {albumOptions.map((album) => (
                      <MenuItem key={album.id} value={String(album.id)}>
                        {album.optionLabel} ({album.imagesCount} items)
                      </MenuItem>
                    ))}
                  </TextField>

                  {selectedAlbum ? (
                    <>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            label="Folder title"
                            value={selectedAlbum.title}
                            onChange={(event) =>
                              handleSelectedAlbumFieldChange(
                                "title",
                                event.target.value,
                              )
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            label="Event date"
                            type="date"
                            value={selectedAlbum.eventDate}
                            onChange={(event) =>
                              handleSelectedAlbumFieldChange(
                                "eventDate",
                                event.target.value,
                              )
                            }
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            label="Folder description"
                            value={selectedAlbum.description}
                            onChange={(event) =>
                              handleSelectedAlbumFieldChange(
                                "description",
                                event.target.value,
                              )
                            }
                            fullWidth
                            multiline
                            minRows={3}
                          />
                        </Grid>
                      </Grid>

                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: "flex-start", md: "center" }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Switch
                            checked={selectedAlbum.isActive}
                            onChange={(event) =>
                              handleSelectedAlbumFieldChange(
                                "isActive",
                                event.target.checked,
                              )
                            }
                          />
                          <Typography fontWeight={700}>Folder is active</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1.25}>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeleteAlbum(selectedAlbum.id)}
                            disabled={
                              pendingAlbumDeleteId === String(selectedAlbum.id)
                            }
                            startIcon={<DeleteOutlineIcon />}
                          >
                            {pendingAlbumDeleteId === String(selectedAlbum.id)
                              ? "Deleting..."
                              : "Delete folder"}
                          </Button>
                          <Button
                            variant="contained"
                            onClick={handleUpdateAlbum}
                            disabled={submittingAlbum}
                            startIcon={<EditOutlinedIcon />}
                          >
                            Save folder changes
                          </Button>
                        </Stack>
                      </Stack>

                      {selectedAlbumFolders.length > 0 ? (
                        <>
                          <Divider />

                          <Stack spacing={1.25}>
                            <Typography variant="h6" fontWeight={800}>
                              Child Folders
                            </Typography>
                            <Grid container spacing={2}>
                              {selectedAlbumFolders.map((folder) => (
                                <Grid key={folder.id} size={{ xs: 12, md: 6 }}>
                                  <Card
                                    elevation={0}
                                    sx={{
                                      borderRadius: 3,
                                      border: "1px solid rgba(15, 23, 42, 0.08)",
                                      backgroundColor: "#f8fafc",
                                    }}
                                  >
                                    <CardContent>
                                      <Stack spacing={1}>
                                        <Typography fontWeight={800}>
                                          {folder.title || "Untitled folder"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                          {folder.description || "No description"}
                                        </Typography>
                                        <Button
                                          variant="outlined"
                                          onClick={() => setSelectedAlbumId(String(folder.id))}
                                          sx={{ alignSelf: "flex-start" }}
                                        >
                                          Open folder
                                        </Button>
                                      </Stack>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          </Stack>
                        </>
                      ) : null}

                      <Divider />

                      <Stack spacing={1.5}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="h6" fontWeight={800}>
                            Add Media to Selected Folder
                          </Typography>
                        </Stack>

                        <Grid container spacing={2}>
                          {uploadCascadeLevels.map((level, index) => (
                            <Grid key={`upload-level-${level.depth}`} size={{ xs: 12, md: 6 }}>
                              <TextField
                                select
                                label={index === 0 ? "Root folder" : `Subfolder level ${index}`}
                                value={level.selectedId}
                                onChange={(event) =>
                                  handleUploadFolderLevelChange(index, event.target.value)
                                }
                                fullWidth
                              >
                                {level.options.map((folder) => (
                                  <MenuItem key={folder.id} value={String(folder.id)}>
                                    {folder.title || "Untitled folder"}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                          ))}
                        </Grid>

                        {uploadTargetAlbum ? (
                          <Alert severity="info" sx={{ borderRadius: 3 }}>
                            Upload target: {uploadTargetAlbum.path || uploadTargetAlbum.title}
                          </Alert>
                        ) : null}

                        {selectedUploadFiles.length > 0 ? (
                          <Alert severity="info" sx={{ borderRadius: 3 }}>
                            {selectedUploadFiles.length} file
                            {selectedUploadFiles.length > 1 ? "s" : ""} selected
                            with total size {formatBytes(totalUploadSize)}. If
                            the server returns `413`, split the upload into
                            smaller batches.
                          </Alert>
                        ) : null}

                        {uploadRows.map((row, index) => (
                          <Card
                            key={`${index}-${row.files[0]?.name || "empty"}`}
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              border: "1px solid rgba(15, 23, 42, 0.08)",
                              backgroundColor: "#f8fafc",
                            }}
                          >
                            <CardContent>
                              <Stack spacing={2}>
                                <Stack
                                  direction={{ xs: "column", md: "row" }}
                                  spacing={2}
                                  alignItems={{ xs: "stretch", md: "center" }}
                                >
                                  <Button
                                    component="label"
                                    variant="outlined"
                                    sx={{ alignSelf: { xs: "stretch", md: "flex-start" } }}
                                  >
                                    {row.files.length > 0
                                      ? "Change selected media"
                                      : "Choose bulk media"}
                                    <input
                                      hidden
                                      type="file"
                                      accept="image/*,video/*"
                                      multiple
                                      onChange={(event) =>
                                        handleUploadRowChange(
                                          index,
                                          "files",
                                          Array.from(event.target.files || []),
                                        )
                                      }
                                    />
                                  </Button>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ flex: 1 }}
                                  >
                                    {row.files.length > 0
                                      ? `${row.files.length} file${
                                          row.files.length > 1 ? "s" : ""
                                        } selected`
                                      : "No media selected yet."}
                                  </Typography>
                                </Stack>

                                <Grid container spacing={2}>
                                  <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField
                                      label="Starting display order"
                                      type="number"
                                      value={row.startDisplayOrder}
                                      onChange={(event) =>
                                        handleUploadRowChange(
                                          index,
                                          "startDisplayOrder",
                                          event.target.value,
                                        )
                                      }
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12, md: 8 }}>
                                    <TextField
                                      select
                                      label="Status"
                                      value="active"
                                      fullWidth
                                      disabled
                                    >
                                      <MenuItem value="active">Active</MenuItem>
                                    </TextField>
                                  </Grid>
                                  <Grid size={{ xs: 12 }}>
                                    <TextField
                                      label="Common description (optional)"
                                      value={row.description}
                                      onChange={(event) =>
                                        handleUploadRowChange(
                                          index,
                                          "description",
                                          event.target.value,
                                        )
                                      }
                                      fullWidth
                                      multiline
                                      minRows={2}
                                    />
                                  </Grid>
                                </Grid>

                                {row.files.length > 0 ? (
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      borderRadius: 2,
                                      bgcolor: "white",
                                      border: "1px solid rgba(15, 23, 42, 0.08)",
                                      maxHeight: 180,
                                      overflow: "auto",
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={700}
                                      sx={{ mb: 1 }}
                                    >
                                      Selected files
                                    </Typography>
                                    <Stack spacing={0.75}>
                                      {row.files.map((file) => (
                                        <Typography
                                          key={file.name}
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {file.name}
                                        </Typography>
                                      ))}
                                    </Stack>
                                  </Box>
                                ) : null}
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}

                        <Button
                          variant="contained"
                          size="large"
                          onClick={handleUploadImages}
                          disabled={submittingImages}
                          startIcon={
                            submittingImages ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <AddPhotoAlternateIcon />
                            )
                          }
                          sx={{
                            borderRadius: 999,
                            py: 1.35,
                            textTransform: "none",
                            fontWeight: 700,
                          }}
                        >
                          {submittingImages ? "Uploading media..." : "Upload Media"}
                        </Button>
                      </Stack>

                      <Divider />

                      <Stack spacing={1.5}>
                        <Typography variant="h6" fontWeight={800}>
                          Existing Media
                        </Typography>
                        {albumLoading ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography color="text.secondary">
                              Loading folder media...
                            </Typography>
                          </Stack>
                        ) : selectedAlbumImages.length === 0 ? (
                          <Typography color="text.secondary">
                            No media added to this folder yet.
                          </Typography>
                        ) : (
                          <Grid container spacing={2}>
                            {selectedAlbumImages.map((image) => (
                              <Grid key={image.id} size={{ xs: 12, md: 6 }}>
                                <Card
                                  elevation={0}
                                  sx={{
                                    height: "100%",
                                    borderRadius: 3,
                                    border: "1px solid rgba(15, 23, 42, 0.08)",
                                  }}
                                >
                                  {image.imageUrl ? (
                                    image.imageType === "video" ? (
                                      <Box
                                        component="video"
                                        src={image.imageUrl}
                                        controls
                                        preload="metadata"
                                        sx={{
                                          width: "100%",
                                          height: 220,
                                          objectFit: "cover",
                                          backgroundColor: "#0f172a",
                                        }}
                                      />
                                    ) : (
                                      <Box
                                        component="img"
                                        src={image.imageUrl}
                                        alt={image.title || "Gallery image"}
                                        sx={{
                                          width: "100%",
                                          height: 220,
                                          objectFit: "cover",
                                        }}
                                      />
                                    )
                                  ) : null}
                                  <CardContent>
                                    <Stack spacing={1.25}>
                                      <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        spacing={1}
                                      >
                                        <Box>
                                          <Typography fontWeight={800}>
                                            {image.title || "Untitled image"}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            Order: {image.displayOrder}
                                          </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            label={image.imageType === "video" ? "Video" : "Image"}
                                          />
                                          <Chip
                                            size="small"
                                            color={image.isActive ? "success" : "default"}
                                            label={image.isActive ? "Active" : "Inactive"}
                                          />
                                        </Stack>
                                      </Stack>
                                      {image.description ? (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {image.description}
                                        </Typography>
                                      ) : null}
                                      <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => handleDeleteImage(image.id)}
                                        disabled={
                                          pendingImageDeleteId === String(image.id)
                                        }
                                        startIcon={<DeleteOutlineIcon />}
                                        sx={{ alignSelf: "flex-start" }}
                                      >
                                        {pendingImageDeleteId === String(image.id)
                                          ? "Deleting..."
                                          : "Delete image"}
                                      </Button>
                                    </Stack>
                                  </CardContent>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Stack>
                    </>
                  ) : (
                    <Typography color="text.secondary">
                      {loading
                        ? "Loading albums..."
                        : "Create your first folder to begin adding media."}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
}

export default AddGalleryImages;
